import { FC, ReactNode } from 'react';
import {
  Alert as ChakraAlert,
  AlertProps as ChakraAlertProps,
  AlertDescription,
  AlertTitle,
  Box,
  Icon as ChakraIcon,
  keyframes,
  Spinner,
} from '@chakra-ui/react';
import { TEST_ID_LOADING_INDICATOR } from 'app/utils/test/constants';
import { ReactComponent as Checkmark } from 'resources/images/checkmark.svg';
import { ReactComponent as RefreshSvg } from 'resources/images/refresh-loader.svg';
import { AlertIcon } from 'app/store/Alerts/slice.types';

interface AlertProps extends ChakraAlertProps {
  title?: string;
  description?: string;
  icon?: AlertIcon;
  iconPosition?: 'left' | 'right';
  iconSpinAnimation?: boolean;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const STATUS_ICON_MAP: Record<AlertIcon, ReactNode> = {
  [AlertIcon.LOADING]: (
    <Spinner
      data-testid={`snackbar-${TEST_ID_LOADING_INDICATOR}`}
      minW="20px"
      minH="20px"
    />
  ),
  [AlertIcon.SUCCESS]: (
    <ChakraIcon
      as={Checkmark}
      width="24px"
      height="20px"
      data-testid="snackbar-success-icon"
      color="G.80"
    />
  ),
  [AlertIcon.REFRESH]: (
    <ChakraIcon
      as={RefreshSvg}
      width="24px"
      height="20px"
      data-testid="snackbar-refresh-icon"
      animation={`${spin} infinite 1.5s linear`}
    />
  ),
};

export const Alert: FC<AlertProps> = ({
  title,
  description,
  icon,
  iconPosition = 'right',
  iconSpinAnimation,
  ...rest
}) => {
  const Icon = icon ? STATUS_ICON_MAP[icon] : null;
  const isClickable = Boolean(rest.onClick);

  return (
    <ChakraAlert
      {...rest}
      cursor={isClickable ? 'pointer' : 'default'}
      data-testid="alert"
    >
      {iconPosition === 'left' && Icon}
      <Box
        ml={iconPosition === 'left' ? 'auto' : 0}
        mr={iconPosition === 'right' ? 'auto' : 0}
      >
        {title && (
          <AlertTitle
            data-testid="alert-title"
            fontWeight={description ? 600 : 400}
            wordBreak="break-all"
          >
            {title}
          </AlertTitle>
        )}
        {description && (
          <AlertDescription data-testid="alert-message" wordBreak="break-all">
            {description}
          </AlertDescription>
        )}
      </Box>
      {iconPosition === 'right' && Icon}
    </ChakraAlert>
  );
};
